import { LayoutGroup, motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { ImageVersions } from "@domain/media/dtos";
import { useImage } from "@domain/media/hooks";
import { Media } from "@domain/media/media";
import { useChangeEffect, useWindowSize } from "@utils/hooks";
import { Z } from "@ui/styles/zIndex";
import { Gallery } from "./gallery";

interface ImageViewProps extends React.HTMLAttributes<HTMLDivElement> {
	image: Media;
	imageStyle?: ReturnType<typeof css>;
	imageVersion: ImageVersions;
	gallery?: Media[];
	keepStaticImage?: boolean;
	zoomDisabled?: boolean;
	cover?: boolean;
	onError?: () => void;
	alt?: string;
	fullSize?: boolean;
}

export const ImageView: React.FC<ImageViewProps> = ({
	image,
	imageStyle,
	imageVersion,
	gallery,
	zoomDisabled,
	keepStaticImage,
	onError,
	cover,
	alt,
	fullSize,
	...htmlProps
}) => {
	const [expanded, setExpanded] = useState(false);

	const { imageUrl, zoomUrl, loaded, error, zoomLoaded } = useImage(image, imageVersion, expanded);
	const [placeholderRemoved, setPlaceholderRemoved] = useState(false);

	useChangeEffect(() => {
		if (loaded) {
			const timeout = setTimeout(() => setPlaceholderRemoved(true), 500);
			return () => clearTimeout(timeout);
		}
		return undefined;
	}, [loaded]);

	useChangeEffect(() => {
		if (error) {
			onError?.();
		}
	}, [error]);

	const { width, height } = useWindowSize();
	const w = image.metadata?.width;
	const h = image.metadata?.height;

	const [finalWidth, finalHeight] = useMemo(() => {
		const reducedWidth = 0.95 * width;
		const reducedHeight = 0.95 * height;
		if (!w || !h) {
			return [undefined, undefined];
		}

		const screenRatio = width / height;
		const imageRatio = w / h;
		if (w <= reducedWidth && h <= reducedHeight) {
			return [w, h];
		}
		if (imageRatio > screenRatio) {
			return [reducedWidth, reducedWidth / imageRatio];
		}
		return [imageRatio * reducedHeight, reducedHeight];
	}, [width, height, w, h]);

	return (
		<Container {...htmlProps}>
			{!placeholderRemoved && !!image.metadata && (
				<Placeholder style={{ opacity: loaded ? 0 : 1 }} url={image.metadata.placeholder} imageStyle={imageStyle} />
			)}
			<LayoutGroup>
				{expanded ? (
					<>
						{
							<StaticImage
								key="fake"
								style={{ opacity: gallery || keepStaticImage ? 1 : 0 }}
								src={imageUrl}
								$imageStyle={imageStyle}
							/>
						}
						<Gallery
							onClose={() => setExpanded(false)}
							items={gallery ?? [image]}
							initialIndex={gallery?.findIndex((img) => img.id === image.id)}
							renderItem={(img) =>
								img.id === image.id ? (
									<Container>
										<ZoomedImage
											layoutId={img.id}
											src={zoomLoaded ? zoomUrl : imageUrl}
											w={finalWidth}
											h={finalHeight}
										/>
									</Container>
								) : (
									<ZoomedImageView image={img} fullSize imageVersion={ImageVersions.LARGE} zoomDisabled />
								)
							}
						/>
					</>
				) : (
					<StaticImage
						w={fullSize ? finalWidth : ["image/gif", "image/webp"].includes(image.mimeType) ? 400 : undefined}
						h={fullSize ? finalHeight : undefined}
						$zoomDisabled={zoomDisabled}
						alt={alt}
						layoutId={!zoomDisabled && placeholderRemoved ? image.id : undefined}
						src={imageUrl}
						onClick={zoomDisabled ? undefined : () => setExpanded(true)}
						$imageStyle={imageStyle}
					/>
				)}
			</LayoutGroup>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	//height: 100%;
`;

const Placeholder = styled.div.withConfig({
	shouldForwardProp: (prop) => !["url", "imageStyle"].includes(prop),
})<{ url?: string; imageStyle?: ReturnType<typeof css> }>`
	${({ imageStyle }) => imageStyle ?? undefined};
	position: absolute;
	z-index: ${Z.over};
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-image: url(${({ url }) => url});
	background-color: transparent;
	background-repeat: no-repeat;
	background-position: 50% 50%;
	background-size: cover;
	filter: blur(10px);
	transition: opacity 0.2s linear;
`;

const StaticImage = styled(motion.img)<{
	$imageStyle?: ReturnType<typeof css>;
	$zoomDisabled?: boolean;
	w?: number;
	h?: number;
}>`
	${({ $imageStyle }) => $imageStyle ?? undefined};
	${({ $zoomDisabled }) => !$zoomDisabled && "cursor: zoom-in;"}
	width: 100%;
	${({ w }) => w && `width: ${w}px;`}
	${({ h }) => h && `height: ${h}px;`}
	height: 100%;
	max-width: 100%;
	max-height: 100%;
	visibility: visible !important;
	object-fit: cover;
`;

const ZoomedImage = styled(StaticImage)`
	object-fit: contain;
	cursor: zoom-out;
`;

export const ZoomedImageView = styled(ImageView)`
	img {
		object-fit: contain;
		cursor: zoom-out;
	}
`;
